/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react';
import {
  Collapse,
  Row,
  Card,
  Button,
} from 'reactstrap';
import { useMutation } from '@apollo/react-hooks';
import CardBody from 'reactstrap/lib/CardBody';
import CardTitle from 'reactstrap/lib/CardTitle';
import DraggableList from 'react-draggable-list';
import Spinner from 'reactstrap/lib/Spinner';
import TemplateCard from './TemplateCard';
import CategoryInfos from './CategoryInfos';
import { UPDATE_TEMPLATE_CATEGORY } from '../../../mutation/mutation';

const removeTypenamesInCategories = (categories, singleCategory) => {
  if (categories !== null) {
    return categories.map((category, templateIndex) => {
      const modifiableCategory = category;
      delete modifiableCategory.__typename;
      modifiableCategory.rank = templateIndex;
      modifiableCategory.subCategories.map((subCategory) => {
        const modifiableSubCategory = subCategory;
        delete modifiableSubCategory.__typename;
        return modifiableSubCategory;
      });
      return modifiableCategory;
    });
  }
  // eslint-disable-next-line no-param-reassign
  delete singleCategory.__typename;
  return {
    ...singleCategory,
    subCategories: singleCategory.subCategories
      ? singleCategory.subCategories.map((subCategory) => {
        const modifiableSubCategory = subCategory;
        delete modifiableSubCategory.__typename;
        return modifiableSubCategory;
      })
      : undefined,
  };
};

const AddDeleteCategoryButtons = ({
  category,
  isSubCategory,
  onAddCategory,
  onDeleteCategory,
  showAddCategory,
}) => {
  const confirmDeleteCategory = () => {
    if (isSubCategory) {
      // eslint-disable-next-line no-undef
      const confirm = window.confirm(`⚠️ Are you sure to delete category '${category.titleEn || category._id}' ?`);
      if (!confirm) {
        return;
      }
    } else {
      // eslint-disable-next-line no-undef
      const confirm = window.prompt(
        `⚠️ Enter admin password to remove category folder '${category.titleEn || category._id}':`,
      ) === 'admin';
      if (!confirm) {
        return;
      }
    }
    onDeleteCategory(category);
  };

  return (
    <div style={styles.lastButtonsContainer}>
      {(!isSubCategory && showAddCategory) && (
        <Button
          color="primary"
          type="button"
          onClick={onAddCategory}
          style={styles.buttons}
        >
          {`Add a subCategory in '${category.titleEn || category._id}'`}
        </Button>
      )}
      <Button
        color="danger"
        type="button"
        onClick={confirmDeleteCategory}
        style={styles.buttonDelete}
      >
        Remove
      </Button>
    </div>
  );
};

const CategoryCard = ({
  index,
  category,
  isSubCategory = false,
  categoriesArray,
  onChangeCategoryPosition,
  onChangeTemplatePosition,
  loadingUpdateTemplatesRank,
  onCreateCategory,
  onDeleteCategory,
  updateTemplate,
  updateCategory,
  templatesList,
}) => {
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [categoryTemplates, setCategoryTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [updateTemplateCategory] = useMutation(UPDATE_TEMPLATE_CATEGORY);

  const templatesOrderCompare = (first, second) => {
    if (!first.ranks || !second.ranks) return 1;
    const foundFirstCategoryRank = first.ranks
      .find((categoryRank) => categoryRank.id === category._id);
    const foundSecondCategoryRank = second.ranks
      .find((categoryRank) => categoryRank.id === category._id);

    if (foundFirstCategoryRank && foundSecondCategoryRank) {
      if (foundFirstCategoryRank.rank < foundSecondCategoryRank.rank) return -1;
      if (foundFirstCategoryRank.rank > foundSecondCategoryRank.rank) return 1;
    }

    return 0;
  };

  const openCard = () => {
    if (!isCardOpen && isSubCategory === true) {
      const templatesFoundForCategory = templatesList.filter((template) => (
        template.status === 'ACCEPTED'
        && (template.subCategories.find((categoryId) => categoryId === category._id) !== undefined)
      ));
      templatesFoundForCategory.sort(templatesOrderCompare);
      setCategoryTemplates(templatesFoundForCategory);
    }
    setIsCardOpen(!isCardOpen);
  };

  useEffect(() => {
    const templatesFoundForCategory = templatesList.filter((template) => (
      template.status === 'ACCEPTED'
      && (template.subCategories.find((categoryId) => categoryId === category._id) !== undefined)
    )).sort((a, b) => (a.ranks[category._id] || 0) - (b.ranks[category._id] || 0));
    templatesFoundForCategory.sort(templatesOrderCompare);
    setCategoryTemplates(templatesFoundForCategory);
  }, [templatesList]);

  const randomizeCategoryTemplates = () => {
    const newList = categoryTemplates.sort(() => (Math.random() > 0.5 ? 1 : -1));
    setCategoryTemplates(newList);
    onChangeTemplatePosition({ newList, categoryId: category._id });
  };

  const onMoveEnd = (newList) => {
    setCategoryTemplates(newList);
    onChangeTemplatePosition({ newList, categoryId: category._id });
  };

  const onTogglePremium = async (template, newPremiumValue) => {
    const newSubCategories = template.subCategories || [];

    if (!newPremiumValue && !newSubCategories.includes('FREE_TODAY_MOST_USED')) {
      newSubCategories.push('FREE_TODAY_MOST_USED');
    } else {
      const freeTodayCategoryIndex = newSubCategories.findIndex((categoryId) => categoryId === 'FREE_TODAY_MOST_USED');

      if (index !== -1) {
        newSubCategories.splice(freeTodayCategoryIndex, 1);
      }
    }
    await updateTemplate({
      variables: {
        templateId: template._id,
        updatedTemplate: {
          premium: newPremiumValue,
          subCategories: newSubCategories,
        },
      },
    });
  };

  const onStartAddCategory = () => {
    if (!isCardOpen) openCard();
    setIsAddingCategory(true);
  };

  const onCategoryInfoUpdate = async (categoryUpdated, isSubCategoryToUpdate = false) => {
    if (isSubCategory) {
      await updateCategory(categoryUpdated, isSubCategory);
      return;
    }

    const modifiableCategory = isSubCategoryToUpdate ? category : categoryUpdated;

    if (isSubCategoryToUpdate) {
      const subCategoryIndex = category.subCategories.findIndex(
        (subCategory) => subCategory._id === categoryUpdated._id,
      );
      if (subCategoryIndex === -1) return;
      modifiableCategory.subCategories[subCategoryIndex] = categoryUpdated;
    }
    await updateTemplateCategory({
      variables: {
        updatedCategory: removeTypenamesInCategories(null, modifiableCategory),
      },
    });
  };

  return (
    <Card style={styles.card({ index, isSubCategory })}>
      <CardBody>
        <CardTitle onClick={openCard} style={styles.cardTitle}>
          <div style={category.hidden ? { color: 'lightgray' } : undefined}>
            {(isSubCategory)
              ? (
                <h5>
                  {category.pinned ? '📌' : ''}
                  {!category.isExcludeRandom ? '✔️' : ''}
                  {category.titleEn || category._id}
                </h5>
              )
              : (
                <h3>{category.titleEn || category._id}</h3>
              )}
          </div>
        </CardTitle>
        {(isCardOpen)
          && (
            <Collapse isOpen={isCardOpen}>
              <CategoryInfos
                category={category}
                categoriesArray={categoriesArray}
                onChangeCategoryPosition={onChangeCategoryPosition}
                index={index}
                onCancelModify={() => {}}
                modifying
                isSubCategory={isSubCategory}
                onEndModify={(updatedCategory) => onCategoryInfoUpdate(updatedCategory)}
              />
              {isSubCategory && (
                <div style={styles.modifyCategoryButtonContainer}>
                  <Button
                    color="primary"
                    type="button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    style={styles.buttons}
                  >
                    {showTemplates ? 'Hide ' : 'Show'}
                    Templates
                  </Button>
                </div>
              )}
              {(category.subCategories) && (
                <Row style={styles.subCategoryCardContainer}>
                  {category.subCategories.map((subCategory, subIndex) => (
                    <CategoryCard
                      key={subCategory._id}
                      categoriesArray={category.subCategories}
                      index={subIndex}
                      category={subCategory}
                      isSubCategory
                      onChangeCategoryPosition={
                        (newIndex) => onChangeCategoryPosition(newIndex, subIndex)
                      }
                      onChangeTemplatePosition={onChangeTemplatePosition}
                      templatesList={templatesList}
                      updateTemplate={updateTemplate}
                      loadingUpdateTemplatesRank={loadingUpdateTemplatesRank}
                      onDeleteCategory={
                        (categoryDescription) => onDeleteCategory({
                          ...categoryDescription,
                          masterCategory: category,
                        })
                      }
                      updateCategory={onCategoryInfoUpdate}
                    />
                  ))}
                </Row>
              )}
              {(isAddingCategory) && (
                <Row style={styles.subCategoryCardContainer}>
                  <CategoryInfos
                    categoriesArray={category.subCategories}
                    onChangeCategoryPosition={onChangeCategoryPosition}
                    index={index}
                    style={styles.card({ index: index + 1, isSubCategory: true })}
                    modifying={isAddingCategory}
                    onCancelModify={() => setIsAddingCategory(false)}
                    onEndModify={async (newCategory) => {
                      setIsAddingCategory(false);
                      await onCreateCategory({
                        masterCategoryIndex: index,
                        newCategory,
                      });
                    }}
                    isSubCategory
                  />
                </Row>
              )}
              {(categoryTemplates.length > 0 && !loadingUpdateTemplatesRank && showTemplates) && (
                <div>
                  <Button
                    color="primary"
                    type="button"
                    onClick={randomizeCategoryTemplates}
                    style={styles.buttons}
                  >
                    Randomize Category Templates
                  </Button>
                  <div style={styles.modifyCategoryButtonContainer}>
                    <DraggableList
                      itemKey={(item) => item._id}
                      list={categoryTemplates}
                      template={TemplateCard}
                      onMoveEnd={onMoveEnd}
                      // eslint-disable-next-line no-undef
                      container={() => document.body}
                      commonProps={{ onTogglePremium }}
                    />
                  </div>
                </div>
              )}
              {(categoryTemplates.length > 0 && !loadingUpdateTemplatesRank && showTemplates) && (
                <div style={styles.modifyCategoryButtonContainer}>
                  <Button
                    color="primary"
                    type="button"
                    onClick={() => setShowTemplates(false)}
                    style={styles.buttons}
                  >
                    Hide Templates
                  </Button>
                </div>
              )}
              {(loadingUpdateTemplatesRank) && (
                <div style={styles.spinnerContainer}>
                  <Spinner />
                </div>
              )}
            </Collapse>
          )}
        <AddDeleteCategoryButtons
          category={category}
          isSubCategory={isSubCategory}
          showAddCategory={isCardOpen}
          onAddCategory={onStartAddCategory}
          onDeleteCategory={
            (categoryToDelete) => onDeleteCategory({ categoryId: categoryToDelete._id })
          }
        />
      </CardBody>
    </Card>
  );
};

const styles = {
  card: ({ index, isSubCategory }) => ({
    backgroundColor: index % 2 ? '#ededed' : 'white',
    borderColor: 'grey',
    borderWidth: 1,
    padding: 10,
    borderRadius: 30,
    marginBottom: 10,
    width: isSubCategory ? 600 : 800,
    alignSelf: 'center',
  }),
  cardTitle: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  subCategoryCardContainer: {
    justifyContent: 'center',
    marginTop: 10,
  },
  spinnerContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modifyCategoryButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  buttons: {
    marginTop: 10,
    marginBottom: 10,
  },
  buttonDelete: {
    position: 'absolute',
    top: 5,
    right: 15,
  },
  lastButtonsContainer: {
    textAlign: 'center',
  },
};

export {
  CategoryCard,
  removeTypenamesInCategories,
};



// WEBPACK FOOTER //
// ./src/components/templates-manager/categories-components/CategoryCard.js